// ----------------------------------------------------
// Logboek24 – Axios API Service (default export: api)
// ----------------------------------------------------
import axios from "axios";
import {
  clearAuthStorage,
  getAccessToken,
  getRefreshToken,
  notifyForcedLogout,
  setAccessToken,
  setRefreshToken,
} from "../utils/authStorage";
import { normalizeApiError } from "../utils/apiError";
import { reportFrontendError } from "./appSignalClient";
import { API_BASE_URL } from "../config/api";

// 👉 Je backend URL
const API_URL = API_BASE_URL;

// 👉 Axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { "Content-Type": "application/json" },
});
let logoutTriggered = false;
let refreshPromise = null;
const BODYLESS_METHODS = new Set(["get", "head"]);

const AUTH_401_HINTS = [
  "token ongeldig",
  "token ontbreekt",
  "token versie ongeldig",
  "token heeft geen geldige sessie",
  "sessie is verlopen",
  "sessie ongeldig",
  "sessie ingetrokken",
  "geen geldige token",
  "ongeldige token",
  "ongeldige sessie",
  "gebruiker bestaat niet meer",
];

function includesAuthInvalidHint(message) {
  const normalized = String(message || "").trim().toLowerCase();
  if (!normalized) return false;
  return AUTH_401_HINTS.some((hint) => normalized.includes(hint));
}

function shouldForceLogoutOn401(error, normalized) {
  const status = Number(normalized?.status || 0);
  if (status !== 401) return false;
  const code = String(normalized?.code || "").trim().toUpperCase();
  if (code && code !== "UNKNOWN_ERROR" && code !== "UNAUTHORIZED") {
    return code.includes("TOKEN") || code.includes("SESSION") || code.includes("AUTH");
  }
  return includesAuthInvalidHint(normalized?.message);
}

function isRefreshRequestPath(path) {
  const value = String(path || "").trim().toLowerCase();
  return value.includes("/auth/refresh");
}

async function refreshTokensSilently() {
  const currentRefreshToken = getRefreshToken();
  if (!currentRefreshToken) {
    throw new Error("NO_REFRESH_TOKEN");
  }

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_URL}/api/auth/refresh`, {
        refreshToken: currentRefreshToken,
      })
      .then((res) => {
        const accessToken = String(res?.data?.accessToken || "").trim();
        const nextRefreshToken = String(res?.data?.refreshToken || "").trim();
        if (!accessToken) {
          throw new Error("REFRESH_NO_ACCESS_TOKEN");
        }
        setAccessToken(accessToken);
        if (nextRefreshToken) {
          setRefreshToken(nextRefreshToken);
        }
        logoutTriggered = false;
        return accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

// ----------------------------------------------------
// 1. AUTH TOKEN AUTOMATISCH MEEGEVEN
// ----------------------------------------------------
api.interceptors.request.use(
  (config) => {
    const method = String(config?.method || "").trim().toLowerCase();
    if (BODYLESS_METHODS.has(method)) {
      config.data = undefined;
      if (config.headers) {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
      }
    }
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ----------------------------------------------------
// 2. ERROR INTERCEPTOR – automatisch uitloggen bij 401
// ----------------------------------------------------
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const normalized = normalizeApiError(error);
    error.apiError = normalized;
    const status = Number(normalized?.status || 0);
    const code = String(normalized?.code || "").trim();
    const method = String(error?.config?.method || "").toUpperCase();
    const path = String(error?.config?.url || "").trim();

    if (status >= 500) {
      reportFrontendError(new Error(normalized.message || "API request mislukt"), {
        module: "frontend.api",
        route: window?.location?.pathname || "",
        code,
        method,
        path,
      });
    }

    if (status === 428 && code === "PASSWORD_RESET_REQUIRED") {
      if (window.location.pathname !== "/wachtwoord-reset") {
        window.location.href = "/wachtwoord-reset";
      }
      return Promise.reject(error);
    }

    const originalRequest = error?.config || null;
    let refreshAttempted = false;
    let refreshFailed = false;

    if (status === 401 && originalRequest && originalRequest._retry !== true) {
      const requestPath = String(originalRequest?.url || path || "").trim();
      if (!isRefreshRequestPath(requestPath) && getRefreshToken()) {
        refreshAttempted = true;
        originalRequest._retry = true;
        try {
          const nextAccessToken = await refreshTokensSilently();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
          return api(originalRequest);
        } catch (_refreshErr) {
          refreshFailed = true;
        }
      }
    }

    const shouldForceLogout = shouldForceLogoutOn401(error, normalized) || refreshFailed;
    if (status === 401) {
      const responseBody = error?.response?.data;
      const authHeaderPresent = Boolean(
        error?.config?.headers?.Authorization || error?.config?.headers?.authorization
      );
      console.warn("[TU/API_DEBUG] 401 received", {
        endpoint: path,
        method,
        status,
        responseBody,
        currentRoute: window?.location?.pathname || "",
        authorizationHeaderPresent: authHeaderPresent,
        refreshAttempted,
        refreshFailed,
        shouldForceLogout,
      });
    }
    if (shouldForceLogout && !logoutTriggered) {
      logoutTriggered = true;
      console.warn("🔐 Sessie ongeldig of ingetrokken — gebruiker uitloggen...");
      clearAuthStorage();
      notifyForcedLogout();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// ----------------------------------------------------
// 3. Named functie exports (optioneel)
// ----------------------------------------------------
export async function login(email, wachtwoord) {
  const res = await api.post("/auth/login", { email, wachtwoord });
  logoutTriggered = false;
  return res.data;
}

export async function getTemplates() {
  const res = await api.get("/survey/templates");
  return res.data;
}

export async function createSurveyEntry(payload) {
  const res = await api.post("/survey", payload);
  return res.data;
}

export async function completeSurvey(id, user) {
  const res = await api.put(`/survey/${id}/complete`, {
    afgerondDoor: user.naam,
  });
  return res.data;
}

// ----------------------------------------------------
// 4. Default export voor al je .post / .get calls
// ----------------------------------------------------
export default api;
